import React, { ButtonHTMLAttributes, FC } from 'react';
import { cn } from '../../utility/cn';

// --- Types ---

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'link' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    loadingText?: string; // Text to show when loading (e.g., "Saving...")
    icon?: React.ReactNode; // Optional icon
}

export const Button: FC<ButtonProps> = ({
    children,
    className,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    loadingText,
    disabled,
    type = 'button',
    icon,
    ...props
}) => {

    // Map Variants to Bootstrap/Metronic Classes
    const variants: Record<ButtonVariant, string> = {
        primary: 'btn-primary',
        secondary: 'secondary-button',
        success: 'btn-success',
        danger: 'btn-danger',
        warning: 'btn-warning',
        info: 'btn-info',
        // Metronic specific: Light button with primary hover text
        light: 'btn-light btn-active-light-primary',
        link: 'btn-link',
        // Ghost: clean look, background on hover
        ghost: 'btn-icon btn-active-light-primary btn-text-primary',
    };

    // Map Sizes
    const sizes: Record<ButtonSize, string> = {
        sm: 'btn-sm',
        md: '',
        lg: 'btn-lg',
    };

    return (
        <button
            type={type}
            disabled={disabled || isLoading}
            className={cn(
                'btn',
                variants[variant],
                sizes[size],
                className
            )}
            {...props}
        >
            {/* Render Loading State */}
            {isLoading && (
                <span className="spinner-border spinner-border-sm align-middle me-2"></span>
            )}

            {/* Render Icon (if not loading) */}
            {!isLoading && icon && <span className="me-2">{icon}</span>}

            {/* Render Text */}
            {isLoading && loadingText ? loadingText : children}
        </button>
    );
};